<template>
    <v-container fluid class="py-6">
        <!-- Top bar -->
        <v-row class="mb-6" align="center" justify="center">
            <v-col cols="12" md="10" lg="9" class="position-relative">
                <div class="d-flex align-center justify-center">
                    <v-btn variant="text" prepend-icon="mdi-arrow-left" class="position-absolute left-0"
                        @click="$router.back()">
                        Back
                    </v-btn>

                    <div class="text-center">
                        <div class="text-h5 font-weight-bold">
                            {{ isAdmin ? 'Listing Review' : 'Listing Details' }}
                        </div>
                        <div class="text-medium-emphasis">
                            {{
                                isAdmin
                                    ? 'Review all details before confirming or denying the listing.'
                                    : 'Manage your listing'
                            }}
                        </div>
                    </div>
                </div>
            </v-col>
        </v-row>

        <!-- Loading state -->
        <v-row v-if="isLoading" align="start" justify="center" class="mb-8">
            <v-col cols="12" md="3">
                <v-skeleton-loader type="card" />
            </v-col>
            <v-col cols="12" md="7">
                <v-skeleton-loader type="card" />
            </v-col>
        </v-row>

        <!-- Content -->
        <v-row v-else-if="currentListing" align="start" justify="center">
            <!-- Left: Agent / Meta -->
            <v-col cols="12" md="2" class="mb-6">
                <PortStopsCard :foundItinerary="currentListing.itinerary" />
            </v-col>
            <!-- Right: Details / Tabs -->
            <v-col cols="12" md="9">
                <ListingStepper v-if="currentListing.current_step > 0" :active-step="currentListing.current_step - 1"
                    class="my-6" />

                <!-- Step banner -->
                <v-alert v-if="banner" class="mb-4" :variant="banner.variant" :color="banner.color" :icon="banner.icon"
                    border="start" :border-color="banner.color">
                    <div class="text-body-1 font-weight-medium" :class="banner.textClass">{{ banner.message }}</div>
                </v-alert>


                <v-card elevation="3" rounded="xl" class="pa-6">
                    <div class="text-subtitle-1 text-medium-emphasis mb-1">
                        {{ currentListing.itinerary?.ship_name }}
                    </div>
                    <div class="text-h5 font-weight-bold mb-4">
                        {{ currentListing.itinerary?.title }}
                    </div>

                    <!-- Tabs -->
                    <v-tabs v-model="activeTab" class="mb-4" density="comfortable">
                        <v-tab value="details" prepend-icon="mdi-information-outline">Listing Details</v-tab>
                        <v-tab v-if="currentListing.current_step > 1" value="guest" prepend-icon="mdi-bed-outline">
                            Guest Info
                        </v-tab>
                        <v-tab v-if="currentListing.current_step > 1" value="documents" prepend-icon="mdi-file-document-multiple-outline">
                            Documents
                        </v-tab>
                        <v-tab v-if="currentListing.current_step > 1" value="workflow" prepend-icon="mdi-list-status">
                            Actions
                        </v-tab>
                    </v-tabs>

                    <v-window v-model="activeTab">
                        <!-- Details -->
                        <v-window-item value="details">
                            <v-row class="gy-6">
                                <!-- Suite Details -->
                                <v-col cols="12" md="4">
                                    <div class="text-subtitle-1 font-weight-medium mb-3">Suite Details</div>
                                    <div><strong>Code:</strong> {{ currentListing.selected_code || '—' }}</div>
                                    <div><strong>Description:</strong> {{ currentListing.description || '—' }}</div>
                                    <div><strong>Room Type:</strong> {{ currentListing.room_type || '—' }}</div>
                                    <div><strong>Guest Limit:</strong> {{ currentListing.guest_limit || '—' }}</div>
                                    <div><strong>Cabin Number:</strong> {{ currentListing.cabin_number || 'TBD' }}</div>

                                    <div class="text-subtitle-1 font-weight-medium mt-6 mb-3">Included Perks</div>
                                    <ul class="pl-4">
                                        <li v-for="perk in currentListing.perks || []" :key="perk">
                                            {{ formatPerk(perk) }}
                                        </li>
                                        <li v-if="!(currentListing.perks || []).length" class="text-medium-emphasis">
                                            None</li>
                                    </ul>
                                </v-col>

                                <!-- Booking Info -->
                                <v-col cols="12" md="4">
                                    <div class="text-subtitle-1 font-weight-medium mb-3">Booking Info</div>
                                    <div><strong>Tips Included:</strong> {{ capitalize(currentListing.tips_included) ||
                                        '—' }}</div>
                                    <div><strong>Name Swaps Allowed:</strong> {{ capitalize(currentListing.name_swaps)
                                        || '—' }}</div>
                                    <div><strong>Promote Listing:</strong> {{ capitalize(currentListing.promote) || '—'
                                        }}</div>
                                    <div><strong>Notes:</strong> {{ currentListing.notes || 'None' }}</div>

                                    <div class="text-subtitle-1 font-weight-medium mt-6 mb-3">Cancellation Policy</div>
                                    <template v-if="hasCancellation">
                                        <ul class="pl-4">
                                            <li v-for="(c, idx) in currentListing.cancellation" :key="idx">
                                                {{ formatDate(c.date) || 'TBD' }} — {{ c.percentage || 'TBD' }}% penalty
                                            </li>
                                        </ul>
                                    </template>
                                    <div v-else class="text-medium-emphasis">No cancellation info provided.</div>
                                </v-col>

                                <!-- Pricing / Payout -->
                                <v-col cols="12" md="4">
                                    <div class="text-subtitle-1 font-weight-medium mb-3">Pricing</div>
                                    <div><strong>Cruise Fare:</strong> {{ money(currentListing.cruise_fare) }}</div>
                                    <div><strong>Taxes:</strong> {{ money(currentListing.taxes) }}</div>
                                    <div><strong>Deposit:</strong> {{ money(currentListing.deposit) }}</div>
                                    <div><strong>Total Cost:</strong> {{ currentListing.total_cost ?
                                        money(currentListing.total_cost) : 'TBD' }}</div>

                                    <div class="text-subtitle-1 font-weight-medium mt-6 mb-3">Payment & Payout</div>
                                    <div><strong>Final Payment Due:</strong> {{
                                        formatDate(currentListing.final_payment_date) || '—' }}</div>
                                    <div><strong>Remove Date:</strong> {{ formatDate(currentListing.remove_date) || '—'
                                        }}</div>
                                    <div><strong>Service Fee:</strong> {{ money(currentListing.service_fee) }}</div>
                                    <div class="text-success text-h6 font-weight-bold mt-1">Payout: {{
                                        money(estimatedPayout) }}</div>
                                </v-col>
                            </v-row>
                        </v-window-item>

                        <!-- Guest Info -->
                        <v-window-item value="guest">
                            <div class="text-subtitle-1 font-weight-medium mb-4">Guests</div>
                            <template v-if="booking?.guests?.list.length">
                                <v-card v-for="(guest, index) in booking.guests.list" :key="index" class="mb-4"
                                    variant="flat" rounded="lg">
                                    <v-card-text class="bg-grey-lighten-4 rounded">
                                        <div class="text-medium-emphasis mb-2">Guest {{ index + 1 }}</div>
                                        <div><strong>Name:</strong> {{ guest.firstName }} {{ guest.middleName }} {{
                                            guest.lastName }}</div>
                                        <div><strong>Gender:</strong> {{ guest.gender }}</div>
                                        <div><strong>Date of Birth:</strong> {{ formatDate(guest.dob) }}</div>
                                        <div><strong>Citizenship:</strong> {{ guest.citizenship }}</div>
                                        <div><strong>Language:</strong> {{ display(guest.language) }}</div>

                                        <div v-if="guest.selectedAdditional?.length" class="mt-2">
                                            <strong>Promo Qualifiers:</strong>
                                            <ul class="pl-4">
                                                <li v-for="(item, i) in guest.selectedAdditional" :key="i">{{ item }}
                                                </li>
                                            </ul>
                                        </div>
                                    </v-card-text>
                                </v-card>
                            </template>

                            <div class="text-subtitle-1 font-weight-medium mt-6 mb-3">Booking Preferences</div>
                            <v-row class="mb-3">
                                <v-col cols="12" md="6">
                                    <div><strong>Dining Time:</strong> {{ booking?.dining_time || '—' }}</div>
                                    <div><strong>Bed Preference:</strong> {{ booking?.bed || '—' }}</div>
                                </v-col>
                                <v-col cols="12" md="6">
                                    <div><strong>Phone:</strong> {{ booking?.phone || '—' }}</div>
                                    <div><strong>Email:</strong> {{ booking?.email || '—' }}</div>
                                </v-col>
                            </v-row>
                        </v-window-item>

                        <!-- Guest Documents -->
                        <v-window-item value="documents">
                            <v-row class="gy-4">
                                <!-- Upload panel -->
                                <!--
                                <v-col cols="12" md="5">
                                    <v-sheet class="pa-4 rounded-lg w-100">
                                        <div class="text-subtitle-1 font-weight-medium mb-3">Upload document</div>

                                        <v-select v-model="newDoc.type" :items="docTypes" label="Document type"
                                            variant="outlined" density="comfortable" clearable hide-details="auto"
                                            class="mb-3" />

                                        <v-file-input v-model="newDoc.file" label="Select file (.pdf, .png, .jpg)"
                                            accept=".pdf,.png,.jpg,.jpeg" variant="outlined" density="comfortable"
                                            hide-details="auto" show-size class="mb-3" />

                                        <div class="d-flex ga-2">
                                            <v-btn color="primary" :loading="isUploading" :disabled="!canUpload"
                                                @click="uploadBookingDoc">
                                                Upload
                                            </v-btn>
                                            <v-btn variant="outlined" color="grey" @click="resetUpload"
                                                :disabled="isUploading">
                                                Clear
                                            </v-btn>
                                        </div>
                                    </v-sheet>
                                </v-col>
                            -->

                                <!-- Files table -->
                                <v-col cols="12" md="12">
                                    <v-card elevation="1">
                                        <v-card-title>
                                            Booking Files
                                            <v-spacer />
                                            <small class="text-medium-emphasis">Showing {{ filesPageStart }}–{{
                                                filesPageEnd }} of {{
                                                    filesCount }}</small>
                                        </v-card-title>
                                        <v-card-text>
                                            <v-table>
                                                <thead>
                                                    <tr>
                                                        <th class="text-left">Type</th>
                                                        <th class="text-left">File Name</th>
                                                        <th class="text-left">Uploaded</th>
                                                        <th class="text-left">By</th>
                                                        <th class="text-left">Status</th>
                                                        <th class="text-center">Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr v-for="f in files" :key="f.id">
                                                        <td class="text-capitalize">{{ humanDocType(f.doc_type) }}</td>
                                                        <td class="truncate-cell"
                                                            :title="f.original_name || f.storage_path">
                                                            {{ f.original_name || f.storage_path }}
                                                        </td>
                                                        <td>{{ formatDateTime(f.created_at) }}</td>
                                                        <td>{{ f.uploaded_by || '—' }}</td>

                                                        <td>
                                                            <v-chip v-bind="chipAttrsFor(f.status)" size="small"
                                                                class="font-weight-medium w-100 text-center"
                                                                variant="flat">
                                                                <v-icon :icon="docStatus[f.status].icon" start
                                                                    class="mx-1" />
                                                                {{ docStatus[f.status].label }}
                                                            </v-chip>
                                                        </td>

                                                        <td class="text-right">
                                                            <v-btn size="small" variant="outlined" color="secondary"
                                                                @click="openFile(f)">Open</v-btn>
                                                            <v-btn size="small" variant="text" color="red" class="ml-1"
                                                                @click="removeFile(f)">
                                                                Delete
                                                            </v-btn>
                                                        </td>
                                                    </tr>
                                                    <tr v-if="(files || []).length === 0">
                                                        <td colspan="5" class="text-center text-medium-emphasis py-6">No
                                                            files yet.</td>
                                                    </tr>
                                                </tbody>
                                            </v-table>

                                            <v-row class="mt-4" justify="end">
                                                <v-col cols="auto">
                                                    <v-pagination v-model="filesPage" :length="filesTotalPages"
                                                        :total-visible="5" prev-icon="mdi-chevron-left"
                                                        next-icon="mdi-chevron-right"
                                                        @update:modelValue="fetchBookingFiles" />
                                                </v-col>
                                            </v-row>
                                        </v-card-text>
                                    </v-card>
                                </v-col>
                            </v-row>
                        </v-window-item>

                        <!-- Workflow tab -->
                        <v-window-item value="workflow" class="my-6">
                            <v-card elevation="2">
                                <v-card-title>Workflow</v-card-title>
                                <v-divider />
                                <v-card-text>
                                    <v-timeline side="end" density="compact">
                                        <v-timeline-item v-for="(s, i) in steps" :key="s.key" :dot-color="dotColor(i)"
                                            :icon="stepIcon(i)" fill-dot>
                                            <!-- Header line -->
                                            <div class="d-flex align-center justify-space-between mb-1">
                                                <div class="text-subtitle-1 font-weight-medium">{{ s.label }}</div>
                                                <v-chip class="mx-3"
                                                    :color="isCompleted(i) ? 'green' : (isCurrent(i) && waitingForAdmin()) ? 'orange' : isCurrent(i) ? 'primary' : 'grey'"
                                                    variant="tonal">
                                                    {{ isCompleted(i)
                                                        ? 'Completed'
                                                        : (isCurrent(i) && waitingForAdmin())
                                                            ? 'In Review'
                                                            : isCurrent(i)
                                                                ? 'In Progress'
                                                                : 'Pending'
                                                    }}
                                                </v-chip>
                                            </div>

                                            <!-- Details/actions: ONLY for the current step -->
                                            <div v-if="isCurrent(i)">
                                                <div v-if="s.message" class="text-body-2 text-medium-emphasis mb-3">
                                                    {{ s.message }}
                                                </div>

                                                <!-- Pending Confirmation actions -->
                                                <template v-if="s.key === 'pending_confirmation' && !waitingForAdmin()">
                                                    <v-row class="ga-2">
                                                        <v-col cols="12" md="6">
                                                            <div class="text-subtitle-2 mb-2">Confirm inventory is still
                                                                available?</div>
                                                            <v-btn-toggle
                                                                v-model="form.pending_confirmation.confirmInventory"
                                                                color="primary" rounded="lg" mandatory>
                                                                <v-btn :value="true"
                                                                    prepend-icon="mdi-check">Yes</v-btn>
                                                                <v-btn :value="false"
                                                                    prepend-icon="mdi-close">No</v-btn>
                                                            </v-btn-toggle>
                                                        </v-col>

                                                        <v-col v-if="form.pending_confirmation.confirmInventory"
                                                            cols="12" md="6">
                                                            <div class="text-subtitle-2 mb-2">Upload Supplier Booking
                                                                Confirmation</div>
                                                            <v-file-input v-model="form.pending_confirmation.file"
                                                                accept=".pdf,.png,.jpg,.jpeg" label="Choose file"
                                                                variant="outlined" density="comfortable"
                                                                hide-details="auto" :disabled="isSaving" />
                                                        </v-col>

                                                        <v-col cols="12" class="d-flex ga-2">
                                                            <v-btn color="primary" :loading="isSaving"
                                                                @click="savePendingConfirmation">
                                                                Submit Document
                                                            </v-btn>
                                                        </v-col>
                                                    </v-row>
                                                </template>
                                                <!-- Pending Payment actions -->
                                                <template v-if="s.key === 'pending_payment' && !waitingForAdmin()">
                                                    <v-row class="ga-2">
                                                        <v-col cols="12" md="6">
                                                            <div class="text-subtitle-2 mb-2">Upload Payment Confirmation</div>
                                                            <v-file-input v-model="form.pending_payment.file"
                                                                accept=".pdf,.png,.jpg,.jpeg" label="Choose file"
                                                                variant="outlined" density="comfortable"
                                                                hide-details="auto" :disabled="isSaving" />
                                                        </v-col>

                                                        <v-col cols="12" class="d-flex ga-2">
                                                            <v-btn color="primary" :loading="isSaving"
                                                                @click="savePendingPayment">
                                                                Submit Document
                                                            </v-btn>
                                                        </v-col>
                                                    </v-row>
                                                </template>

                                                <!-- Add more per-step actions here as needed -->
                                                <!-- e.g., pending_payment, final_payment, etc. -->
                                            </div>
                                        </v-timeline-item>
                                    </v-timeline>
                                </v-card-text>
                            </v-card>
                        </v-window-item>



                    </v-window>
                </v-card>
            </v-col>
        </v-row>

        <v-row v-else align="center" justify="center">
            <v-col cols="12" md="8" class="text-center">
                <v-skeleton-loader type="text" class="mb-2" />
                <div class="text-medium-emphasis">Loading listing details…</div>
            </v-col>
        </v-row>

        <!-- Snackbar -->
        <v-snackbar v-model="snackbar.show" :color="snackbar.color" location="bottom right" timeout="2500">
            {{ snackbar.msg }}
        </v-snackbar>
    </v-container>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import { supabase } from '@/supabaseClient'
import ListingStepper from '@/components/ListingStepper.vue'
import PortStopsCard from '@/components/PortStopsCard.vue'
import { useNotifier } from '@/composables/useNotifier'
const { sendNotification } = useNotifier()

/* -------------------------------------------------------
   STORE / ROUTE
------------------------------------------------------- */
const store = useStore()
const route = useRoute()
const user = computed(() => store.state.user || null)
const isAdmin = computed(() => !!user.value?.isAdmin)

/* -------------------------------------------------------
   STATE
------------------------------------------------------- */
const isLoading = ref(true)
const currentListing = ref(null)
const booking = ref(null)
const activeTab = ref('details')

const snackbar = ref({ show: false, msg: '', color: 'success' })

// Files / uploads
const files = ref([])
const filesCount = ref(0)
const filesPage = ref(1)
const filesPageSize = ref(10)
const filesTotalPages = ref(1)

const newDoc = ref({ type: null, file: null })
const isUploading = ref(false)


// Optional if needed elsewhere
const otherUserId = ref(null)
const guestID = ref(null)

// Doc types shown in the select
const docTypes = [
    { value: 'id', title: 'Government ID' },
    { value: 'passport', title: 'Passport' },
    { value: 'agreement', title: 'Signed Agreement' },
    { value: 'invoice', title: 'Invoice/Receipt' },
    { value: 'supplier_confirmation', title: 'Supplier Confirmation' },
    { value: 'other', title: 'Other' },
]

const docStatus = [
    { key: 'under_review', label: 'Under Review', color: 'orange', icon: 'mdi-account-clock' },
    { key: 'approved', label: 'Approved', color: 'green', icon: 'mdi-check-circle' },
    { key: 'denied', label: 'Denied', color: 'red', icon: 'mdi-alert-circle-outline' },
]

function chipAttrsFor(status) {
    const step = docStatus[status]
    return { color: step.color }
}

/* -------------------------------------------------------
   WORKFLOW STEPS (index aligns with numeric current_step)
------------------------------------------------------- */
const steps = [
    { key: 'pending_posting', label: 'Pending Approval', color: 'orange', icon: 'mdi-clock-time-nine-outline', message: 'Your listing pending approval by our team', textClass: 'text-orange-darken-3' },
    { key: 'listing_posted', label: 'Listing Posted', color: 'blue', icon: 'mdi-bullhorn', message: 'Your listing is live and visible to guests.', textClass: 'text-blue-darken-3' },
    { key: 'booking_requested', label: 'Booking Requested', color: 'amber', icon: 'mdi-account-clock', message: 'A booking was requested. Our team is reviewing and will respond promptly.', textClass: 'text-amber-darken-3' },
    { key: 'pending_confirmation', label: 'Pending Confirmation', color: 'indigo', icon: 'mdi-help-circle-outline', message: 'Confirm inventory is still available. Upload Supplier Booking Confirmation with reservation #. Reconfirm payment deadlines.', textClass: 'text-indigo-darken-3' },
    { key: 'pending_payment', label: 'Pending Payment', color: 'deep-orange', icon: 'mdi-cash-clock', message: 'Submit card payment to supplier and upload updated confirmation showing payment received.', textClass: 'text-deep-orange-darken-3' },
    { key: 'booking_confirmed', label: 'Booking Confirmed', color: 'green', icon: 'mdi-check-circle', message: 'Booking confirmed! Final payment is approaching.', textClass: 'text-green-darken-3' },
    { key: 'final_payment', label: 'Final Payment', color: 'teal', icon: 'mdi-cash-check', message: 'Final payment due. Verify totals and due dates.', textClass: 'text-teal-darken-3' },
    { key: 'set_sail', label: 'Set Sail', color: 'cyan', icon: 'mdi-ship-wheel', message: 'Bon voyage! Everything is done for this booking.', textClass: 'text-cyan-darken-3' },
    { key: 'end_summary', label: 'End Summary', color: 'grey', icon: 'mdi-file-document-check', message: 'Wrap-up time. Send the end-of-trip summary and request reviews.', textClass: 'text-grey-darken-2' },
]
const stepMap = new Map(steps.map((s, i) => [s.key, { ...s, index: i }]))

/* Step-related computed helpers */
const currentIdx = computed(() => {
    const s = currentListing.value?.current_step
    if (Number.isFinite(s)) return Math.max(0, Math.min(s, steps.length - 1))
    if (typeof s === 'string' && stepMap.has(s)) return stepMap.get(s).index
    return 0
})

const banner = computed(() => {
    let s = currentListing.value?.current_step
    let step = null
    if (Number.isFinite(s)) step = steps[Math.max(0, Math.min(s, steps.length - 1))]
    else if (typeof s === 'string') step = stepMap.get(s) || steps[0]
    else step = steps[0]

    return { variant: 'tonal', ...step }
})

function isCompleted(i) { return i < currentIdx.value }
function isCurrent(i) { return i === currentIdx.value }
function waitingForAdmin() { return currentListing.value?.awaiting_admin }
function stepIcon(i) { return isCompleted(i) ? 'mdi-check-circle' : (isCurrent(i) ? 'mdi-radiobox-marked' : 'mdi-checkbox-blank-circle-outline') }
function dotColor(i) { return isCompleted(i) ? 'green' : (isCurrent(i) ? 'primary' : 'grey') }

/* -------------------------------------------------------
   ACTION FORMS (per-step)
------------------------------------------------------- */
const form = ref({
    pending_confirmation: {
        confirmInventory: null,
        file: null,
    },
    pending_payment: {
        file: null,
    },
})

// Pending Action
async function savePendingConfirmation() {
    if (form.value.pending_confirmation.confirmInventory == null) {
        toast('Please select Yes or No for inventory availability.', 'warning')
        return
    }
    if (form.value.pending_confirmation.confirmInventory === true) {
        if (!form.value.pending_confirmation.file) {
            toast('Please choose a file to upload.', 'warning')
            return
        }

        // reuse the generic uploader
        newDoc.value = {
            type: 'supplier_confirmation',
            file: form.value.pending_confirmation.file,
        }
        const inserted = await uploadBookingDoc()
        if (!inserted) return
        await sendNotification({ type: 'document_uploaded', forAdmin: true, listingId: currentListing.value.id, meta: { docName: newDoc.value.original_name, bookingId: currentListing.value.booking_id } })

        // If confirmed, advance to "pending_payment"
        if (currentListing.value?.id) {
            const { error } = await supabase
                .from('listings')
                .update({ awaiting_admin: true })
                .eq('id', currentListing.value.id)
            if (!error) currentListing.value.awaiting_admin = true
            await sendNotification({ type: 'booking_confirmed', forAdmin: true, listingId: currentListing.value.id })
        }
    } else {
        await sendNotification({ type: 'booking_confirm_denied', forAdmin: true, listingId: currentListing.value.id })
    }

    // clear the step form
    form.value.pending_confirmation = { confirmInventory: null, file: null }
}

// Pending payment action
async function savePendingPayment() {
        if (!form.value.pending_payment.file) {
            toast('Please choose a file to upload.', 'warning')
            return
        }

        // reuse the generic uploader
        newDoc.value = {
            type: 'invoice',
            file: form.value.pending_payment.file,
        }
        const inserted = await uploadBookingDoc()
        if (!inserted) return
        await sendNotification({ type: 'document_uploaded', forAdmin: true, listingId: currentListing.value.id, meta: { docName: newDoc.value.original_name, bookingId: currentListing.value.booking_id } })

        // If confirmed, advance to "pending_payment"
        if (currentListing.value?.id) {
            const { error } = await supabase
                .from('listings')
                .update({ awaiting_admin: true })
                .eq('id', currentListing.value.id)
            if (!error) currentListing.value.awaiting_admin = true
            await sendNotification({ type: 'payment_uploaded', forAdmin: true, listingId: currentListing.value.id, meta: { bookingId: currentListing.value.booking_id } })
        }

    // clear the step form
    form.value.pending_payment = { file: null }
}

/* -------------------------------------------------------
   COMPUTEDS
------------------------------------------------------- */
const hasCancellation = computed(() =>
    !!currentListing.value?.cancellation?.some(c => c?.date || c?.percentage)
)

const estimatedPayout = computed(() => {
    const total = Number(currentListing.value?.cruise_fare) || 0
    const fee = Number(currentListing.value?.service_fee) || 0
    return total - fee
})

const filesPageStart = computed(() => (filesPage.value - 1) * filesPageSize.value)
const filesPageEnd = computed(() => Math.min(filesPageStart.value + filesPageSize.value, filesCount.value || 0))

const bookingId = computed(() => booking.value?.id || null)
const canUpload = computed(() =>
    !!newDoc.value.type && !!newDoc.value.file && !isUploading.value && !!bookingId.value
)

/* -------------------------------------------------------
   HELPERS
------------------------------------------------------- */
function toast(msg, color = 'success') {
    snackbar.value = { show: true, msg, color }
}
function money(v) {
    const n = Number(v)
    if (Number.isNaN(n)) return '—'
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n)
}
function formatDate(d) {
    if (!d) return '—'
    const dt = new Date(d)
    return isNaN(dt) ? '—' : dt.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}
function formatDateTime(d) {
    if (!d) return '—'
    const dt = new Date(d)
    return isNaN(dt) ? '—' : dt.toLocaleString()
}
function capitalize(v) { return v ? (String(v).charAt(0).toUpperCase() + String(v).slice(1).toLowerCase()) : '—' }
function display(v) { return v || 'Not specified' }
function formatPerk(perk) {
    const map = {
        onboardCredit50: 'Onboard Credit - $50',
        onboardCredit100: 'Onboard Credit - $100',
        other: 'Other',
        specialtyDining: 'Specialty Dining',
        prepaidGratuity: 'Prepaid Gratuity',
        beveragePackage: 'Beverage Package',
        internetPackage: 'Internet Package',
        shoreExcursionCredit: 'Shore Excursion Credit'
    }
    return map[perk] || perk
}
function humanDocType(t) {
    const found = docTypes.find(d => d.value === t)
    return found ? found.title : (t || 'Unknown')
}

/* -------------------------------------------------------
   DATA LOADERS
------------------------------------------------------- */
async function fetchBookingByListingId(listingId) {
    try {
        const { data, error } = await supabase
            .from('bookings')
            .select('*')
            .eq('listing_id', listingId)
            .limit(1)
            .single()

        if (error) throw error
        return data
    } catch (e) {
        // Not found is OK (PGRST116)
        return null
    }
}

async function fetchListing() {
    isLoading.value = true
    try {
        const id = route.params.id
        const { data, error } = await supabase
            .from('listings')
            .select('*')
            .eq('id', id)
            .single()
        if (error) throw error

        currentListing.value = { id, ...data }
        otherUserId.value = currentListing.value.uid

        const b = await fetchBookingByListingId(currentListing.value.id)
        booking.value = b
        guestID.value = b?.user_id || null

        await fetchBookingFiles() // initial page
    } catch (err) {
        console.error('Error fetching listing:', err)
        toast('Failed to load listing.', 'error')
    } finally {
        isLoading.value = false
    }
}

/* -------------------------------------------------------
   FILES (assumes table public.booking_files + bucket booking_docs)
------------------------------------------------------- */
const BUCKET_BOOKING_DOCS = 'booking_docs'       // change if your bucket is named differently
const FILES_TABLE = 'booking_files'              // change if your table is named differently
const USE_PUBLIC_BUCKET = false
const MAX_BYTES = 20 * 1024 * 1024           // 20MB
const ALLOWED_DOC_TYPES = new Set(['id', 'passport', 'agreement', 'invoice', 'other', 'supplier_confirmation'])

// Helpers
function normalizeFileName(name = 'file') {
    // strip accents → slug → keep last extension
    const n = name.normalize('NFKD').replace(/[\u0300-\u036f]/g, '')
    const m = n.match(/^(.*?)(?:\.([^.]+))?$/)
    const stem = (m?.[1] || 'file').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
    const ext = (m?.[2] || '').toLowerCase().replace(/[^a-z0-9]/g, '')
    return ext ? `${stem}.${ext}` : stem
}

function normalizePath(p) {
    if (!p) return ''
    let s = String(p).trim().replace(/^\/+/, '') // no leading slash
    // if someone saved "booking_docs/..." in the DB, strip it (because .from('booking_docs') already sets the bucket)
    if (s.startsWith(`${BUCKET_BOOKING_DOCS}/`)) {
        s = s.slice(BUCKET_BOOKING_DOCS.length + 1)
    }
    return s
}

async function fetchBookingFiles(page = filesPage.value) {
    if (!bookingId.value) {
        files.value = []
        filesCount.value = 0
        filesTotalPages.value = 1
        return
    }

    const from = (page - 1) * filesPageSize.value
    const to = from + filesPageSize.value - 1

    try {
        const { data, count, error } = await supabase
            .from(FILES_TABLE)
            .select('*', { count: 'exact' })
            .eq('booking_id', bookingId.value)
            .order('created_at', { ascending: false })
            .range(from, to)

        if (error) throw error
        files.value = data || []
        filesCount.value = count ?? 0
        filesTotalPages.value = Math.max(1, Math.ceil((filesCount.value || 0) / filesPageSize.value))
    } catch (e) {
        console.error('fetchBookingFiles:', e)
        files.value = []
        filesCount.value = 0
        filesTotalPages.value = 1
    }
}

async function uploadBookingDoc() {
    if (!canUpload.value) return

    isUploading.value = true
    try {
        // pick file + type
        const file = Array.isArray(newDoc.value.file) ? newDoc.value.file[0] : newDoc.value.file
        const docType = String(newDoc.value.type || '').trim()

        // validations
        if (!bookingId.value) throw new Error('Missing booking id')
        if (!file) throw new Error('No file selected')
        if (!ALLOWED_DOC_TYPES.has(docType)) throw new Error('Invalid document type')
        if (file.size > MAX_BYTES) throw new Error('File is too large')

        // path like: <bookingId>/<docType>/<timestamp>_<safeName>
        const safeName = normalizeFileName(file.name || 'document')
        const path = `${bookingId.value}/${docType}/${Date.now()}_${safeName}`

        // upload
        const { error: upErr } = await supabase.storage
            .from(BUCKET_BOOKING_DOCS)
            .upload(path, file, {
                cacheControl: '3600',
                upsert: false,
                contentType: file.type || undefined,
            })
        if (upErr) throw upErr

        // URL (public or signed)
        let publicUrl = null
        if (USE_PUBLIC_BUCKET) {
            const { data: pub } = supabase.storage.from(BUCKET_BOOKING_DOCS).getPublicUrl(path)
            publicUrl = pub?.publicUrl || null
        } else {
            const { data: signed, error: sErr } = await supabase.storage
                .from(BUCKET_BOOKING_DOCS)
                .createSignedUrl(path, 60 * 60 * 24 * 7) // 7 days
            if (sErr) throw sErr
            publicUrl = signed?.signedUrl || null
        }

        // insert DB row (and return it)
        const payload = {
            booking_id: bookingId.value,
            doc_type: docType,
            storage_path: path,
            public_url: publicUrl,          // if private bucket, this will be a signed url
            original_name: file.name,
            uploaded_by: user.value?.email || user.value?.uid || null,
            listing_id: currentListing.value?.id ?? null,
        }

        const { data: inserted, error: insErr } = await supabase
            .from(FILES_TABLE)
            .insert(payload)
            .select()
            .single()
        if (insErr) throw insErr

        // refresh list (set page before fetch)
        filesPage.value = 1
        await fetchBookingFiles(1)

        toast('Document uploaded.')
        resetUpload?.()

        return inserted
    } catch (e) {
        console.error('uploadBookingDoc:', e)
        toast(`Upload failed: ${e?.message || e}`, 'error')
        return null
    } finally {
        isUploading.value = false
    }
}

function resetUpload() {
    newDoc.value = { type: null, file: null }
}

async function openFile(f) {
    try {
        if (!f?.storage_path) throw new Error('No storage path on record')

        const relPath = normalizePath(f.storage_path)

        // 1) Prefer a signed URL (works with private buckets)
        const { data, error } = await supabase
            .storage
            .from(BUCKET_BOOKING_DOCS)
            .createSignedUrl(relPath, 60) // seconds

        if (error) {
            console.error('[openFile] createSignedUrl error:', error)
            throw new Error(error.message || 'Could not create a signed URL')
        }

        const url = data?.signedUrl
        if (!url) {
            console.warn('[openFile] createSignedUrl returned no signedUrl.')
            throw new Error('Signed URL missing from response')
        }

        window.open(url, '_blank', 'noopener')
    } catch (e) {
        console.error('[openFile] ', e)
        toast(e?.message || 'File URL unavailable.', 'error')
    }
}

async function removeFile(f) {
    try {
        // Delete storage object if we have the path
        if (f.storage_path) {
            const { error: delErr } = await supabase.storage.from(BUCKET_BOOKING_DOCS).remove([f.storage_path])
            if (delErr) console.warn('storage remove warning:', delErr.message)
        }
        // Delete DB row
        const { error } = await supabase.from(FILES_TABLE).delete().eq('id', f.id)
        if (error) throw error

        toast('File removed.')
        await fetchBookingFiles(filesPage.value)
    } catch (e) {
        console.error('removeFile:', e)
        toast('Failed to remove file.', 'error')
    }
}


/* -------------------------------------------------------
   LIFECYCLE
------------------------------------------------------- */
onMounted(fetchListing)
watch(filesPage, (p) => fetchBookingFiles(p))
</script>


<style scoped>
.position-absolute.left-0 {
    left: 0;
}
</style>
